import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import config from '../../config';
import { getLocalStorageItem, removeLocalStorageItem } from '../../utils/storage';
import { Api, ApiConfig, SKIP_WORKSPACE_ID_HEADER } from '../api';
import type { IntegrationResource } from '../brandGroupService';
import {
  BUSTLY_CORE_PRODUCT_CODE,
  PLUGIN_PRODUCT_CODE,
  PRODUCT_CONTEXT_HEADER,
  withPluginProductHeaders,
  withProductHeaders,
} from '../productContext';
import { resolveOAuthAuthorizeCallbackUrl, type OAuthAuthorizeCallbackTarget, type OAuthAuthorizeCallbackResponse } from './oauthAuthorizeCallback';
import { supabase } from './supabase';

// 响应类型定义
export interface BetaStatusResponse {
  betaAccess: boolean;
}

export interface RedeemCodeResponse {
  success: boolean;
  message?: string;
}

export interface ShopBinding {
  id: string;
  shopId: string;
  domain: string;
}

export interface ShopInfo {
  id: string;
  domain: string;
  brandName: string;
  platform: string;
  timezone: string;
  physicFlag: number;
}

export interface ShopifyConnectRequest {
  shop_domain: string;
  redirect_url: string;
  connection_alias?: string;
  connection_scene?: ConnectionScene;
  onboarding_flow?: boolean;
  axiosRequestConfig?: AxiosRequestConfig;
}

export type ConnectionScene = 'onboarding' | 'admin';

export interface ShopifyConnectResponse {
  shop: string;
  authorizationUrl: string;
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  eligibilityMessage?: string | null;
  message?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
  currentStoreCount?: number | null;
  storeLimit?: number | null;
  upgradeTarget?: StoreLimitUpgradeTarget | null;
}

export interface ShopifyDisconnectRequest {
  shop_domain: string;
  connection_scene?: ConnectionScene;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface ShopifyDisconnectResponse {
  success: boolean;
  message?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  userName: string;
}

export interface EmailRegistrationStatus {
  exists: boolean;
  needsPasswordSetup: boolean;
  passwordSetupFlow?: 'workspace_invite' | 'email_code_login' | 'passwordless' | string;
}

export interface Workspace {
  id: string;
  name: string;
  logoUrl: string | null;
  role: string;
  membershipRole?: string | null;
  workspaceRoleKey?: string | null;
  workspaceRoleName?: string | null;
  isAdminRole?: boolean;
}

export interface WorkspacePlan {
  planId: string;
  planName: string;
  planCode: string;
  tier: string;
  status: string;
  startAt: string;
  trialEndAt: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  currency: string;
  billingCycle: string;
  provider: string;
  // Optional summary-style fields from upgraded backend DTOs
  title?: string;
  badge?: string;
  supportingText?: string;
  billingProvider?: string | null;
  paymentProvider?: string | null;
  paymentStatus?: string | null;
  payment_status?: string | null;
  shopifyPaymentStatus?: string | null;
  shopify_payment_status?: string | null;
  paymentPending?: boolean | null;
  payment_pending?: boolean | null;
  isPaymentPending?: boolean | null;
  is_payment_pending?: boolean | null;
  shopifyPaymentPending?: boolean | null;
  shopify_payment_pending?: boolean | null;
  reasonCode?: string | null;
  reason_code?: string | null;
  planCreditRefreshCadence?: string | null;
  plan_credit_refresh_cadence?: string | null;
  actionLabel?: string;
  statusLabel?: string;
  statusMessage?: string;
  primaryAction?: string;
  primaryActionMode?: string;
  nextBillingAt?: string | null;
  expiresAt?: string | null;
  displayBudgetMicrousd?: number | null;
  displayUsedMicrousd?: number | null;
  displayRemainingMicrousd?: number | null;
  usageStatus?: string | null;
  bannerType?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  bannerAction?: string | null;
  cards?: any;
}

export interface RegistrationGate {
  enabled: boolean;
  showGate: boolean;
  status: 'PENDING' | 'APPROVED' | 'BYPASSED' | 'REJECTED' | 'NOT_REQUIRED' | 'DISABLED' | string;
  source?: string | null;
  reasonCode?: string | null;
  action?: 'CONTACT_SALES' | 'CONTINUE_ONBOARDING' | string | null;
  message?: string | null;
}

export interface RegistrationGateContactRequest {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  industry: string;
  companySize?: string;
  message?: string;
  fullName?: string;
  workTitle?: string;
  storeUrl?: string;
  preferredContactMethod?: string;
}

export interface VerifyAuthResponse {
  userInfo: UserInfo;
  workspace: Workspace | null;
  workspacePlan: WorkspacePlan | null;
  registrationGate?: RegistrationGate | null;
}

export interface EmailCodeVerifyResponse {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires_at?: number;
  token_type?: string;
}

export interface OnboardingResumeWorkspace {
  id: string;
  name: string;
  logoUrl?: string | null;
  ownerId: string;
  settings?: Record<string, any> | null;
  status: string;
  memberCount?: number | null;
  currentUserRole?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductWorkspaceSubscription {
  id?: string | null;
  planId?: string | null;
  status?: string | null;
  isInTrial?: boolean | null;
  currentPeriodEnd?: string | null;
  trialEndAt?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  createdAt?: string | null;
  provider?: string | null;
  planCode?: string | null;
  planName?: string | null;
  planTier?: string | null;
  billingCycle?: string | null;
  planLabel?: string | null;
  planBadge?: string | null;
  statusLabel?: string | null;
  statusMessage?: string | null;
  actionLabel?: string | null;
  nextAction?: string | null;
}

export interface ProductWorkspace extends OnboardingResumeWorkspace {
  membershipRole?: string | null;
  workspaceRoleKey?: string | null;
  workspaceRoleName?: string | null;
  subscription?: ProductWorkspaceSubscription | null;
}

export interface OnboardingWorkspaceResumeResponse {
  shouldResume: boolean;
  workspace?: OnboardingResumeWorkspace | null;
  currentStep?: number | null;
  reason?: string | null;
  connectedPlatform?: string | null;
}

export interface NangoConnectTokenRequest {
  integration_id: string;
  connection_scene?: ConnectionScene;
  onboarding_flow?: boolean;
  [key: string]: any;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface NangoConnectTokenResponse {
  data?: { token?: string; requiresAccountSelection?: boolean };
  result?: { token?: string; requiresAccountSelection?: boolean };
  token?: string;
  requiresAccountSelection?: boolean;
  action?: string;
  url?: string;
  connectionId?: string;
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  eligibilityMessage?: string | null;
  message?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
  currentStoreCount?: number | null;
  storeLimit?: number | null;
  upgradeTarget?: StoreLimitUpgradeTarget | null;
}

export interface ZernioConnectInitRequest {
  platform: 'meta-ads' | 'facebook-page' | 'instagram' | string;
  returnUrl?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface ZernioConnectInitResponse {
  authUrl?: string;
  flowId: string;
  platform: string;
  completed?: boolean;
  workspaceIntegrationId?: string | number;
}

export interface ZernioDisconnectRequest {
  platform: 'meta-ads' | 'facebook-page' | 'instagram' | string;
  workspaceIntegrationId?: number;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface ZernioDisconnectResponse {
  platform?: string;
  disconnected?: boolean;
  message?: string;
}

export interface NangoUnbindRequest {
  integration_id: string;
  connection_id?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface CloudAgentExchangeTokenRequest {
  workspaceId: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface CloudAgentExchangeTokenResponse {
  exchangeToken: string;
  workspaceId: string;
  expiresAt?: string;
}

export interface CloudAgentConnectBundleRequest {
  workspaceId: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface CloudAgentConnectBundleResponse {
  workspaceId: string;
  routingMode: string;
  workspaceHost?: string;
  httpBaseUrl?: string;
  wsUrl: string;
  gatewayToken: string;
  expiresAt?: string;
}

// ============ Aliexpress Integration API Types ============

export interface AliexpressAuthorizeRequest {
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface AliexpressAuthorizeData {
  auth_url: string;
  redirect_uri: string;
}

export interface AliexpressAuthorizeResponse {
  code: string;
  message: string;
  status: string;
  data: AliexpressAuthorizeData;
}

export interface AliexpressUnbindRequest {
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface AliexpressUnbindData {
  workspace_id: string;
  status: string;
}

export interface AliexpressUnbindResponse {
  code: string;
  message: string;
  status: string;
  data: AliexpressUnbindData;
}

// ============ Wix Integration API Types ============

export interface WixConnectInitRequest {
  workspace_id: string;
  redirect_url?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface WixConnectInitResponse {
  ok: boolean;
  platform: 'wix';
  mode: 'webhook_polling' | 'redirect_callback';
  install_url?: string;
  callback_url: string | null;
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  eligibilityMessage?: string | null;
  message?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
  currentStoreCount?: number | null;
  storeLimit?: number | null;
  upgradeTarget?: StoreLimitUpgradeTarget | null;
}

export interface WixConnectDiscoverRequest {
  workspace_id: string;
  not_before?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface WixConnectDiscoverResponse {
  ok: boolean;
  platform: 'wix';
  workspace_id: string;
  connected: boolean;
  instance_id: string | null;
  site_id: string | null;
  source?: string;
  candidate_count?: number;
}

export interface WixConnectCompleteRequest {
  workspace_id: string;
  callback_params: Record<string, any>;
  callback_url?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface WixConnectCompleteResponse {
  ok: boolean;
  platform: 'wix';
  workspace_id: string;
  instance_id: string;
  site_id: string | null;
  integration_id: number;
  message: string;
}

export interface WixDisconnectRequest {
  workspace_id: string;
  instance_id?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface WixDisconnectResponse {
  ok: boolean;
  workspace_id: string;
  instance_id?: string;
  deactivated_mappings: number;
  deactivated_integrations: number;
  message: string;
}

// ============ Slack Integration API Types ============

export interface SlackConnectInitRequest {
  workspace_id: string;
  redirect_uri?: string;
  return_url?: string;
}

export interface SlackConnectInitResponse {
  ok: boolean;
  platform: 'slack';
  mode: 'redirect_callback';
  workspace_id: string;
  authorize_url: string;
  redirect_uri: string;
  state: string;
}

export interface SlackConnectCompleteRequest {
  code: string;
  state: string;
  redirect_uri?: string;
}

export interface SlackConnectCompleteResponse {
  ok: boolean;
  platform: 'slack';
  workspace_id: string;
  team_id: string;
  team_name: string | null;
  integration_id: number;
  message: string;
}

export interface SlackDisconnectRequest {
  workspace_id: string;
  team_id?: string;
}

export interface SlackDisconnectResponse {
  ok: boolean;
  workspace_id: string;
  team_id?: string;
  deactivated_mappings: number;
  deactivated_integrations: number;
  message: string;
}

// ============ Feishu Integration API Types ============

export interface FeishuConnectInitRequest {
  workspace_id: string;
  region?: 'feishu';
  provider?: 'feishu';
  app_id?: string;
  appId?: string;
  client_id?: string;
  clientId?: string;
  app_secret?: string;
  appSecret?: string;
  client_secret?: string;
  clientSecret?: string;
  scope?: string;
  redirect_uri?: string;
  return_url?: string;
  connection_scene?: 'onboarding' | 'admin';
  onboarding_flow?: boolean;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface FeishuConnectInitResponse {
  ok: boolean;
  platform: 'feishu';
  provider?: 'feishu';
  region?: 'feishu';
  mode: 'redirect_callback';
  workspace_id: string;
  authorize_url?: string | null;
  redirect_uri: string;
  connection?: string | null;
  app_id?: string | null;
  scope?: string | null;
  state?: string;
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  eligibilityMessage?: string | null;
  message?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
}

export interface FeishuConnectCompleteRequest {
  code: string;
  state: string;
  redirect_uri?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface FeishuConnectCompleteResponse {
  ok: boolean;
  platform: 'feishu';
  provider?: 'feishu';
  region?: 'feishu';
  workspace_id: string;
  tenant_key?: string | null;
  tenant_name?: string | null;
  integration_id?: number;
  connection?: string;
  message: string;
}

export interface FeishuDisconnectRequest {
  workspace_id: string;
  tenant_key?: string;
  connection_id?: string;
  external_account_id?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface FeishuDisconnectResponse {
  ok: boolean;
  workspace_id: string;
  tenant_key?: string;
  deactivated_integrations: number;
  token_storage_cleared?: boolean;
  remote_token_revoked?: boolean;
  message: string;
}

// ============ DingTalk Integration API Types ============

export interface DingTalkConnectInitRequest {
  workspace_id: string;
  region?: 'dingtalk';
  provider?: 'dingtalk';
  app_id?: string;
  appId?: string;
  client_id?: string;
  clientId?: string;
  app_secret?: string;
  appSecret?: string;
  client_secret?: string;
  clientSecret?: string;
  scope?: string;
  redirect_uri?: string;
  return_url?: string;
  connection_scene?: 'onboarding' | 'admin';
  onboarding_flow?: boolean;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface DingTalkConnectInitResponse {
  ok: boolean;
  platform: 'dingtalk';
  provider?: 'dingtalk';
  region?: 'dingtalk';
  mode: 'redirect_callback';
  workspace_id: string;
  authorize_url?: string | null;
  redirect_uri: string;
  connection?: string | null;
  app_id?: string | null;
  scope?: string | null;
  state?: string;
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  eligibilityMessage?: string | null;
  message?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
}

export interface DingTalkConnectCompleteRequest {
  code: string;
  state: string;
  redirect_uri?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface DingTalkConnectCompleteResponse {
  ok: boolean;
  platform: 'dingtalk';
  provider?: 'dingtalk';
  region?: 'dingtalk';
  workspace_id: string;
  corp_id?: string | null;
  corp_name?: string | null;
  integration_id?: number;
  connection?: string;
  message: string;
}

export interface DingTalkDisconnectRequest {
  workspace_id: string;
  corp_id?: string;
  connection_id?: string;
  external_account_id?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface DingTalkDisconnectResponse {
  ok: boolean;
  workspace_id: string;
  corp_id?: string;
  deactivated_integrations: number;
  token_storage_cleared?: boolean;
  message: string;
}

// ============ Workspace Integrations API Types ============

export type ConnectionBackend = 'NANGO' | 'BUSTLY' | 'NATIVE' | 'ZERNIO';

export interface CreateBustlyConnectSessionRequest {
  integrationKey: string;
  connectionScene?: ConnectionScene;
  onboardingFlow?: boolean;
  redirectUri?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface CreateBustlyConnectSessionResponse {
  connectUrl: string;
  flowId?: string;
  integrationKey: string;
  connectionBackend: 'BUSTLY';
  handoff?: {
    completionMode?: string;
    connectionIdQueryParameter?: string;
    statusQueryParameter?: string;
    errorQueryParameter?: string;
  };
}

export interface RegisterBustlyIntegrationRequest {
  externalConnectionId: string;
  integrationKey: string;
  label?: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

// Workspace Integration Mapping types (from API response)
export interface WorkspaceIntegrationMapping {
  id?: number | string;
  slugId?: string | null;
  label?: string | null;
  last_synced_at: string | null;
  role?: string;
  updated_at: string;
  user_id: string;
  connection_id: string;
  connectionId?: string | null;
  nango_connection_id?: string | null;
  nangoConnectionId?: string | null;
  connection_backend?: ConnectionBackend | null;
  connectionBackend?: ConnectionBackend | null;
  external_connection_id?: string | null;
  externalConnectionId?: string | null;
  integration_key?: string | null;
  integrationKey?: string | null;
  slug_id?: string | null;
  shop_domain?: string | null;
  shopDomain?: string | null;
  store_url?: string | null;
  storeUrl?: string | null;
  name?: string | null;
  account_name?: string | null;
  authorized_account_name?: string | null;
  authorizedAccountName?: string | null;
  authorized_account_email?: string | null;
  authorizedAccountEmail?: string | null;
  authorized_account_id?: string | null;
  authorizedAccountId?: string | null;
  external_account_name?: string | null;
  externalAccountName?: string | null;
  external_account_email?: string | null;
  externalAccountEmail?: string | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
  external_account_type?: string | null;
  externalAccountType?: string | null;
  connection_mode?: string | null;
  connectionMode?: string | null;
  authType?: string | null;
  native?: boolean | null;
  created_at: string;
  status?: string;
  customer_id?: string;
  time_zone?: string | null;
  currency_code?: string | null;
  account_id?: string;
  provider?: string;
  provider_config_key?: string;
  environment?: string;
  direct?: boolean;
  mapping_status?: string;
  account_status?: string;
  store_name?: string | null;
  shop_name?: string | null;
  logo_url?: string | null;
  sub_account_count?: number;
  connectedBy?: string | null;
  connectedByName?: string | null;
  connectedAt?: string | null;
  yourAccess?: 'DISABLED' | 'READ' | 'WRITE' | string | null;
  canManageAccess?: boolean | null;
  /** Trial eligibility belongs to the connected account, not the provider. */
  restrictionState?: StoreTrialEligibilityState | null;
  eligibilityState?: StoreTrialEligibilityState | null;
  allowsFreshTrial?: boolean | null;
  eligibilityMessage?: string | null;
  message?: string | null;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
  currentStoreCount?: number | null;
  storeLimit?: number | null;
  upgradeTarget?: StoreLimitUpgradeTarget | null;
  sub_accounts?: Array<{
    label?: string | null;
    mapping_id?: number | string | null;
    account_name?: string | null;
    customer_id?: string;
    account_id?: string;
    currency_code?: string | null;
    time_zone?: string | null;
    manager_id?: string | null;
  }>;
}

export interface GoogleAdsSelectableSubAccount {
  id: string;
  name?: string | null;
  is_manager?: boolean;
  manager_account?: boolean;
  manager_id?: string | null;
  currency_code?: string | null;
  time_zone?: string | null;
  timezone?: string | null;
  status?: string | null;
  login_customer_id?: string | null;
  eligible?: boolean;
  selected?: boolean;
  active_selected?: boolean;
  disabled_reason?: string | null;
}

export interface GoogleAdsAccountGroup {
  login_customer_id?: string | null;
  login_customer_name?: string | null;
  sub_accounts?: GoogleAdsSelectableSubAccount[];
}

export interface GoogleAdsAccountsResponse {
  hasIntegration: boolean;
  integrationId?: number | string;
  connectionId?: string;
  accounts: GoogleAdsAccountGroup[];
  selectedCustomerIds?: string[];
  defaultSelectedCustomerIds?: string[];
  eligibleCustomerIds?: string[];
}

export interface GetGoogleAdsAccountsRequest {
  connectionId?: string | null;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface RefreshGoogleResourcesRequest {
  provider: 'google-analytics' | 'google-search-console';
  connectionId: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface RefreshGoogleResourcesResponse {
  integrationId?: number | string;
  connectionId?: string;
  provider?: string;
  resourceCount: number;
  resourcesReady: boolean;
  resources?: IntegrationResource[];
}

export interface SaveGoogleResourceSelectionRequest {
  provider: 'google-analytics' | 'google-search-console';
  connectionId?: string | null;
  selectedResourceKeys: string[];
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface SaveGoogleResourceSelectionResponse {
  hasIntegration: boolean;
  integrationId?: number | string;
  connectionId?: string;
  provider?: string;
  selectedResourceKeys?: string[];
  deactivatedResourceKeys?: string[];
  resourceCount?: number;
}

export interface DisconnectGoogleResourceRequest {
  provider: 'google-ads' | 'google-analytics' | 'google-search-console';
  connectionId: string;
  resourceKey: string;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface DisconnectGoogleResourceResponse {
  hasIntegration: boolean;
  appDisconnected: boolean;
  integrationId?: number | string;
  connectionId?: string;
  provider?: string;
  resourceKey?: string;
  disconnectedResourceKey?: string;
  remainingActiveResourceCount?: number;
}

export interface SaveGoogleAdsAccountSelectionRequest {
  connection_id?: string | null;
  selected_customer_ids: string[];
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface SaveGoogleAdsAccountSelectionResponse {
  hasIntegration: boolean;
  integrationId?: number | string;
  connectionId?: string;
  selectedCustomerIds?: string[];
  newlyActivatedCustomerIds?: string[];
  deactivatedCustomerIds?: string[];
  initialSyncJobCount?: number;
}

// Workspace Integration type (from API response)
export interface WorkspaceIntegrationAPI {
  connected: boolean;
  environment: string;
  mappings: WorkspaceIntegrationMapping[];
  updated_at: string;
  provider: string;
  created_at: string;
  profile_data: any;
  external_account_name?: string | null;
  externalAccountName?: string | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
  external_account_type?: string | null;
  externalAccountType?: string | null;
  connection_mode?: string | null;
  connectionMode?: string | null;
  authType?: string | null;
  native?: boolean | null;
  restrictionState?: StoreTrialEligibilityState | null;
  eligibilityState?: StoreTrialEligibilityState | null;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  ctaAction?: string | null;
  allowsFreshTrial?: boolean;
  message?: string | null;
  eligibilityMessage?: string | null;
  trialRemainingDays?: number | null;
  currentWorkspaceId?: string | null;
  currentWorkspaceName?: string | null;
  currentStoreCount?: number | null;
  storeLimit?: number | null;
  upgradeTarget?: StoreLimitUpgradeTarget | null;
  connectedBy?: string | null;
  connectedByName?: string | null;
  connectedAt?: string | null;
  yourAccess?: 'DISABLED' | 'READ' | 'WRITE' | string | null;
  canManageAccess?: boolean | null;
}

// Response format for workspace integrations API
export interface WorkspaceIntegrationsAPIResponse {
  [provider: string]: WorkspaceIntegrationAPI[];
}

export interface WorkspaceConnectionAccessGrant {
  userId: string;
  email?: string | null;
  name?: string | null;
  workspaceRole?: string | null;
  accessLevel?: 'DISABLED' | 'READ' | 'WRITE' | string | null;
  canManageAccess?: boolean | null;
  grantSource?: string | null;
  grantedBy?: string | null;
  grantedAt?: string | null;
}

export interface WorkspaceGoogleAdsCustomerAccessOption {
  customerId: string;
  name?: string | null;
  loginCustomerId?: string | null;
  selectionSource?: string | null;
  selectedAt?: string | null;
}

export interface WorkspaceGoogleAdsCustomerAccessGrant {
  userId: string;
  customerId: string;
  accessLevel?: 'READ' | 'WRITE' | string | null;
  grantSource?: string | null;
  grantedBy?: string | null;
  grantedAt?: string | null;
}

export interface WorkspaceConnectionAccessGrantsResponse {
  integrationId: number | string;
  accessGrants: WorkspaceConnectionAccessGrant[];
  googleAdsCustomers?: WorkspaceGoogleAdsCustomerAccessOption[];
  googleAdsCustomerGrants?: WorkspaceGoogleAdsCustomerAccessGrant[];
}

export interface UpdateWorkspaceConnectionAccessGrant {
  userId: string;
  accessLevel: 'DISABLED' | 'READ' | 'WRITE';
  canManageAccess?: boolean;
  googleAdsCustomerIds?: string[];
}

export interface UpdateWorkspaceConnectionAccessGrantsRequest {
  grants: UpdateWorkspaceConnectionAccessGrant[];
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface ZernioConnectStatusResponse {
  flowId: string;
  platform?: string;
  status?: string;
  terminal?: boolean;
  completed?: boolean;
  failed?: boolean;
  errorCode?: string | null;
  errorMessage?: string | null;
  workspaceIntegrationId?: number | string | null;
  integrationStatus?: string | null;
  integrationErrorCode?: string | null;
  integrationErrorMessage?: string | null;
}

export interface GetWorkspaceIntegrationsResponse {
  code: string;
  message: string;
  status: string;
  data: WorkspaceIntegrationsAPIResponse;
}

export interface ManualApiKeyConnectRequest {
  provider: string;
  apiKey?: string;
  accountLabel?: string;
  storeUrl?: string;
  clientId?: string;
  clientSecret?: string;
  scope?: string;
  connection_scene?: ConnectionScene;
  onboarding_flow?: boolean;
}

export interface JudgeMeOAuthInitRequest {
  workspace_id: string;
  platform: 'judgeme' | string;
  redirect_uri: string;
  return_uri?: string;
  connection_name?: string;
  connection_alias?: string;
  shop_domain?: string;
  shopDomain?: string;
  store_domain?: string;
  storeDomain?: string;
  connection_scene?: ConnectionScene;
  onboarding_flow?: boolean;
}

export interface JudgeMeOAuthInitResponse {
  ok: boolean;
  platform: string;
  mode: 'redirect_callback' | 'server_callback';
  workspace_id: string;
  authorize_url: string;
  redirect_uri: string;
  return_uri?: string;
  state: string;
}

export interface JudgeMeOAuthCompleteRequest {
  workspace_id: string;
  platform: 'judgeme' | string;
  code: string;
  state: string;
  redirect_uri?: string;
}

export interface JudgeMeOAuthCompleteResponse {
  ok: boolean;
  success: boolean;
  connected?: boolean;
  platform?: string;
  workspace_id?: string;
  integration_id?: number | string;
  connection?: string;
  account_name?: string;
}

export interface JudgeMeOAuthDisconnectRequest {
  workspace_id: string;
  platform: 'judgeme' | string;
  connection_id?: string;
  integration_id?: number | string;
}

export interface JudgeMeOAuthDisconnectResponse {
  ok: boolean;
  success: boolean;
  disconnected?: boolean;
  count?: number;
}
export interface ManualApiKeyQueryRequest {
  target: string;
  country?: string;
  startDate?: string;
  endDate?: string;
  granularity?: string;
  limit?: number;
}

export interface UpdateWorkspaceIntegrationLabelRequest {
  label?: string | null;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface UpdateWorkspaceIntegrationLabelResponse {
  id: number;
  workspaceId: string;
  platform: string;
  platformAccountId?: string | null;
  nangoConnectionId?: string | null;
  slugId?: string | null;
  label?: string | null;
  status: string;
  updatedAt?: string;
}

export interface UpdateGoogleAdsCustomerLabelRequest {
  label?: string | null;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface UpdateGoogleAdsCustomerLabelResponse {
  integrationId: number | string;
  customerId: string;
  label?: string | null;
}

/**
 * A label owned by one independently selectable account/resource within a
 * workspace integration (for example, a GA property or a Search Console
 * site). It is deliberately separate from the integration-level label.
 */
export interface UpdateWorkspaceIntegrationResourceLabelRequest {
  resourceType: string;
  resourceKey: string;
  label?: string | null;
  axiosRequestConfig?: AxiosRequestConfig;
}

export interface UpdateWorkspaceIntegrationResourceLabelResponse {
  integrationId: number | string;
  resourceType: string;
  resourceKey: string;
  label?: string | null;
}

// Trial eligibility metadata is returned by the actual connect APIs.
export type StoreTrialEligibilityState =
  | 'eligible'
  | 'trial_resume_allowed'
  | 'already_bound'
  | 'trial_valid'
  | 'trial_expired'
  | 'plan_expired'
  | 'payment_past_due'
  | 'store_limit_reached'
  | 'integration_group_limit_reached'
  | 'total_connection_limit_reached';
export type StoreLimitUpgradeTarget = 'TEAM' | 'SALES';

// OAuth 上下文类型
export interface OAuthContext {
  client_id: string;
  redirect_uri: string | null;
  fallback_redirect_uri?: string | null;
  device_id: string | null;
  login_trace_id: string | null;
}

// OAuth 授权响应类型
export interface OAuthAuthorizeResponse extends OAuthAuthorizeCallbackResponse {}

export class AuthService {
  private api: Api;

  constructor(config: ApiConfig) {
    // 统一使用传入的 baseURL
    this.api = new Api({
      ...config,
    });
  }

  /**
   * 调用 Supabase Edge Function
   */
  private async invokeSupabaseFunction<T>(name: string, body: Record<string, any>): Promise<T> {
    const { data, error } = await supabase.functions.invoke(name, {
      body,
    });

    if (error) {
      // 某些浏览器环境下 supabase-js 可能抛出 FunctionsFetchError（请求未发出/被中断）
      // 增加一次直连 fetch 回退，避免“假失败”。
      if ((error.message || '').includes('Failed to send a request to the Edge Function')) {
        try {
          return await this.invokeSupabaseFunctionViaFetch<T>(name, body);
        } catch (fallbackError: any) {
          throw new Error(fallbackError?.message || error.message || `Failed to invoke function: ${name}`);
        }
      }
      throw new Error(error.message || `Failed to invoke function: ${name}`);
    }

    // Supabase Function 可能返回 { error, details } 结构
    if (data?.error) {
      throw new Error(data.details || data.error);
    }

    return data as T;
  }

  /**
   * Supabase Edge Function 直连回退（用于处理 invoke 的网络层异常）
   */
  private async invokeSupabaseFunctionViaFetch<T>(name: string, body: Record<string, any>): Promise<T> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    if (!supabaseUrl || !anonKey) {
      throw new Error(`Missing Supabase env for function fallback: ${name}`);
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const accessToken = session?.access_token;
    if (!accessToken) {
      throw new Error('No active Supabase session. Please sign in again and retry.');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body || {}),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const details = payload?.details || payload?.error || payload?.message;
      throw new Error(details || `Edge Function ${name} failed (${response.status})`);
    }

    if (payload?.error) {
      throw new Error(payload.details || payload.error);
    }

    return payload as T;
  }

  /**
   * 验证用户身份
   */
  public async verifyAuth(axiosRequestConfig?: AxiosRequestConfig): Promise<VerifyAuthResponse> {
    try {
      const response = await this.api.get<VerifyAuthResponse>('/api/auth/verify', axiosRequestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async checkEmailRegistered(
    email: string,
    axiosRequestConfig?: AxiosRequestConfig,
  ): Promise<EmailRegistrationStatus> {
    return this.api.get<EmailRegistrationStatus>(
      `/api/auth/check-email?email=${encodeURIComponent(email)}`,
      axiosRequestConfig,
    );
  }

  public async sendEmailCode(
    email: string,
    axiosRequestConfig?: AxiosRequestConfig,
  ): Promise<{ message?: string }> {
    return this.api.post<{ message?: string }>(
      '/api/auth/email-code/send',
      { email },
      axiosRequestConfig,
    );
  }

  public async verifyEmailCode(
    email: string,
    code: string,
    axiosRequestConfig?: AxiosRequestConfig,
  ): Promise<EmailCodeVerifyResponse> {
    return this.api.post<EmailCodeVerifyResponse>(
      '/api/auth/email-code/verify',
      { email, code },
      axiosRequestConfig,
    );
  }

  public async submitRegistrationGateContact(request: RegistrationGateContactRequest): Promise<RegistrationGate> {
    return this.api.post<RegistrationGate>('/api/auth/registration-gate/contact', request);
  }

  /**
   * 获取用户的 beta 状态
   */
  public async getBetaStatus(): Promise<BetaStatusResponse> {
    try {
      const response = await this.api.get<BetaStatusResponse>('/api/beta/status');
      return response;
    } catch (error) {
      return { betaAccess: true };
    }
  }

  /**
   * 兑换邀请码
   * @param code 邀请码
   */
  public async redeemInviteCode(code: string): Promise<RedeemCodeResponse> {
    try {
      const response = await this.api.post<RedeemCodeResponse>('/api/beta/redeem', { code });
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, message: 'Verify code failed' };
    }
  }

  /**
   * 绑定 Shopify 店铺
   * @param accessToken Shopify Admin API access token
   * @param domain Shopify 店铺域名
   */
  public async bindShop({ accessToken, domain }: { accessToken: string; domain: string }): Promise<string> {
    try {
      const response = await this.api.post<string>('/api/shop-binding/bind', {
        accessToken,
        domain,
      });
      return response;
    } catch (error) {
      console.error('Bind shop failed:', error);
      return 'fail';
    }
  }

  /**
   * Connect to Shopify after Google login
   * @param shopDomain Shopify shop domain
   * @param redirectUrl Redirect URL after connection
   * @param authorization Authorization token
   */
  public async connectShopify({ shop_domain, redirect_url, connection_alias, connection_scene, onboarding_flow, axiosRequestConfig }: ShopifyConnectRequest): Promise<ShopifyConnectResponse> {
    try {
      const response = await this.api.post<ShopifyConnectResponse>(
        '/api/shopify/connect',
        {
          shop_domain: shop_domain,
          redirect_url: redirect_url,
          connection_alias,
          connection_scene,
          onboarding_flow: onboarding_flow ?? connection_scene === 'onboarding',
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        axiosRequestConfig,
      );
      return response;
    } catch (error) {
      console.error('Connect Shopify failed:', error);
      throw error;
    }
  }

  /**
   * Disconnect from Shopify store
   * @param data Shopify disconnect data
   * @returns Promise with disconnect result
   */
  public async disconnectShopify({ shop_domain, connection_scene, axiosRequestConfig }: ShopifyDisconnectRequest): Promise<ShopifyDisconnectResponse> {
    try {
      const connectionSceneQuery = connection_scene
        ? `&connection_scene=${encodeURIComponent(connection_scene)}`
        : '';
      const response = await this.api.post<ShopifyDisconnectResponse>(`/api/shopify/disconnect?shop_domain=${shop_domain}${connectionSceneQuery}`, undefined, axiosRequestConfig);
      return response;
    } catch (error) {
      console.error('Disconnect Shopify failed:', error);
      throw error;
    }
  }

  /**
   * 获取 Google Ads 账户列表
   */
  public async getNangoGoogleAdsAccounts(request: GetGoogleAdsAccountsRequest = {}): Promise<GoogleAdsAccountsResponse> {
    try {
      const { connectionId, axiosRequestConfig } = request;
      const response = await this.api.get<GoogleAdsAccountsResponse>('/api/nango/user/google-ads/accounts', {
        ...axiosRequestConfig,
        params: {
          ...(axiosRequestConfig?.params || {}),
          ...(connectionId ? { connection_id: connectionId } : {}),
        },
      });
      return response;
    } catch (error) {
      console.error('Get Google Ads accounts failed:', error);
      throw error;
    }
  }

  /**
   * 保存 Google Ads customer_id 选择
   */
  public async saveNangoGoogleAdsAccountSelection(request: SaveGoogleAdsAccountSelectionRequest): Promise<SaveGoogleAdsAccountSelectionResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = request;
      const response = await this.api.put<SaveGoogleAdsAccountSelectionResponse>(
        '/api/nango/user/google-ads/accounts/selection',
        requestData,
        axiosRequestConfig,
      );
      return response;
    } catch (error) {
      console.error('Save Google Ads account selection failed:', error);
      throw error;
    }
  }

  /** Refresh GA/GSC secondary resources from Nango after OAuth. */
  public async refreshNangoGoogleResources(
    request: RefreshGoogleResourcesRequest,
  ): Promise<RefreshGoogleResourcesResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = request;
      const response = await this.api.post<RefreshGoogleResourcesResponse>(
        '/api/nango/user/google/resources/refresh',
        {
          provider: requestData.provider,
          connection_id: requestData.connectionId,
        },
        axiosRequestConfig,
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /** Save selected GA/GSC child resources after OAuth confirmation. */
  public async saveNangoGoogleResourceSelection(
    request: SaveGoogleResourceSelectionRequest,
  ): Promise<SaveGoogleResourceSelectionResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = request;
      const response = await this.api.put<SaveGoogleResourceSelectionResponse>(
        '/api/nango/user/google/resources/selection',
        {
          provider: requestData.provider,
          connection_id: requestData.connectionId,
          selected_resource_keys: requestData.selectedResourceKeys,
        },
        axiosRequestConfig,
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /** Disconnect one Google Ads customer, GA property, or GSC site from Settings. */
  public async disconnectNangoGoogleResource(
    request: DisconnectGoogleResourceRequest,
  ): Promise<DisconnectGoogleResourceResponse> {
    const { axiosRequestConfig, ...requestData } = request;
    return this.api.post<DisconnectGoogleResourceResponse>(
      '/api/nango/user/google/resources/disconnect',
      {
        provider: requestData.provider,
        connection_id: requestData.connectionId,
        resource_key: requestData.resourceKey,
      },
      axiosRequestConfig,
    );
  }

  /**
   * 获取 Nango 连接 Token
   */
  public async getNangoConnectToken(data: NangoConnectTokenRequest): Promise<NangoConnectTokenResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = data;
      const response = await this.api.post<NangoConnectTokenResponse>('/api/nango/connect-token', requestData, axiosRequestConfig);
      return response;
    } catch (error) {
      console.error('Get Nango connect token failed:', error);
      throw error;
    }
  }

  /**
   * 解绑 Nango 集成
   */
  public async unbindNangoIntegration(data: NangoUnbindRequest): Promise<void> {
    try {
      const { axiosRequestConfig, ...requestData } = data;
      await this.api.post('/api/nango/unbind', requestData, axiosRequestConfig);
    } catch (error) {
      console.error('Unbind Nango integration failed:', error);
      throw error;
    }
  }

  public async initZernioConnect(data: ZernioConnectInitRequest): Promise<ZernioConnectInitResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = data;
      return await this.api.post<ZernioConnectInitResponse>('/api/connection-hub/connect/init', requestData, axiosRequestConfig);
    } catch (error) {
      console.error('Init Zernio connect failed:', error);
      throw error;
    }
  }

  public async getZernioConnectStatus(flowId: string, axiosRequestConfig?: AxiosRequestConfig): Promise<ZernioConnectStatusResponse> {
    try {
      return await this.api.get<ZernioConnectStatusResponse>('/api/connection-hub/connect/status', {
        ...axiosRequestConfig,
        params: {
          ...(axiosRequestConfig?.params || {}),
          flowId,
        },
      });
    } catch (error) {
      console.error('Get Zernio connect status failed:', error);
      throw error;
    }
  }

  public async disconnectZernioIntegration(data: ZernioDisconnectRequest): Promise<ZernioDisconnectResponse> {
    try {
      const { axiosRequestConfig, ...requestData } = data;
      return await this.api.post<ZernioDisconnectResponse>('/api/connection-hub/disconnect', requestData, axiosRequestConfig);
    } catch (error) {
      console.error('Disconnect Zernio integration failed:', error);
      throw error;
    }
  }

  /**
   * Aliexpress 授权 - 获取授权 URL
   * API 会自动提取 response.data.data，返回的是 AliexpressAuthorizeData
   */
  public async authorizeAliexpress({ axiosRequestConfig }: AliexpressAuthorizeRequest): Promise<AliexpressAuthorizeData> {
    try {
      const response = await this.api.get<AliexpressAuthorizeData>('/api/aliexpress/authorize', axiosRequestConfig);
      return response;
    } catch (error) {
      console.error('Aliexpress authorize failed:', error);
      throw error;
    }
  }

  /**
   * Aliexpress 解绑
   * API 会自动提取 response.data.data，返回的是 AliexpressUnbindData
   */
  public async unbindAliexpress({ axiosRequestConfig }: AliexpressUnbindRequest): Promise<AliexpressUnbindData> {
    try {
      const response = await this.api.post<AliexpressUnbindData>('/api/aliexpress/unbind', undefined, axiosRequestConfig);
      return response;
    } catch (error) {
      console.error('Aliexpress unbind failed:', error);
      throw error;
    }
  }

  /**
   * Wix 初始化连接：获取安装 URL
   */
  public async initWixConnect({ workspace_id, redirect_url }: WixConnectInitRequest): Promise<WixConnectInitResponse> {
    return this.invokeSupabaseFunction<WixConnectInitResponse>('wix-connect-init', {
      workspace_id,
      redirect_url,
    });
  }

  /**
   * Wix 安装后发现：轮询 webhook 入库状态并返回候选 instance
   */
  public async discoverWixConnect({ workspace_id, not_before }: WixConnectDiscoverRequest): Promise<WixConnectDiscoverResponse> {
    return this.invokeSupabaseFunction<WixConnectDiscoverResponse>('wix-connect-discover', {
      workspace_id,
      not_before,
    });
  }

  /**
   * Wix 完成连接：回调参数入库并绑定 workspace
   */
  public async completeWixConnect({ workspace_id, callback_params, callback_url }: WixConnectCompleteRequest): Promise<WixConnectCompleteResponse> {
    return this.invokeSupabaseFunction<WixConnectCompleteResponse>('wix-connect-complete', {
      workspace_id,
      callback_params,
      callback_url,
    });
  }

  /**
   * Wix 断开连接
   */
  public async disconnectWix({ workspace_id, instance_id }: WixDisconnectRequest): Promise<WixDisconnectResponse> {
    return this.invokeSupabaseFunction<WixDisconnectResponse>('wix-disconnect', {
      workspace_id,
      instance_id,
    });
  }

  public async initJudgeMeOAuth({
    workspace_id,
    platform,
    redirect_uri,
    return_uri,
    connection_name,
    connection_alias,
    shop_domain,
    shopDomain,
    store_domain,
    storeDomain,
    connection_scene,
    onboarding_flow,
  }: JudgeMeOAuthInitRequest): Promise<JudgeMeOAuthInitResponse> {
    return this.invokeSupabaseFunction<JudgeMeOAuthInitResponse>('judgeme-oauth', {
      action: 'init',
      workspace_id,
      platform,
      redirect_uri,
      return_uri,
      connection_name,
      connection_alias,
      shop_domain: shop_domain || shopDomain || store_domain || storeDomain,
      connection_scene,
      onboarding_flow,
    });
  }

  public async completeJudgeMeOAuth({ workspace_id, platform, code, state, redirect_uri }: JudgeMeOAuthCompleteRequest): Promise<JudgeMeOAuthCompleteResponse> {
    return this.invokeSupabaseFunction<JudgeMeOAuthCompleteResponse>('judgeme-oauth', {
      action: 'callback',
      workspace_id,
      platform,
      code,
      state,
      redirect_uri,
    });
  }

  public async disconnectJudgeMeOAuth({ workspace_id, platform, connection_id, integration_id }: JudgeMeOAuthDisconnectRequest): Promise<JudgeMeOAuthDisconnectResponse> {
    return this.invokeSupabaseFunction<JudgeMeOAuthDisconnectResponse>('judgeme-oauth', {
      action: 'disconnect',
      workspace_id,
      platform,
      connection_id,
      integration_id,
    });
  }
  /**
   * Slack 初始化连接：获取授权 URL
   */
  public async initSlackConnect({ workspace_id, redirect_uri, return_url }: SlackConnectInitRequest): Promise<SlackConnectInitResponse> {
    return this.invokeSupabaseFunction<SlackConnectInitResponse>('slack-connect-init', {
      workspace_id,
      redirect_uri,
      return_url,
    });
  }

  /**
   * Slack 完成连接：处理 OAuth code 并写入绑定
   */
  public async completeSlackConnect({ code, state, redirect_uri }: SlackConnectCompleteRequest): Promise<SlackConnectCompleteResponse> {
    return this.invokeSupabaseFunction<SlackConnectCompleteResponse>('slack-connect-callback', {
      code,
      state,
      redirect_uri,
    });
  }

  /**
   * Slack 断开连接
   */
  public async disconnectSlack({ workspace_id, team_id }: SlackDisconnectRequest): Promise<SlackDisconnectResponse> {
    return this.invokeSupabaseFunction<SlackDisconnectResponse>('slack-disconnect', {
      workspace_id,
      team_id,
    });
  }

  /**
   * Feishu 初始化连接：获取自建 OAuth 授权 URL。
   */
  public async initFeishuConnect(request: FeishuConnectInitRequest): Promise<FeishuConnectInitResponse> {
    const { workspace_id, region, provider, app_id, appId, client_id, clientId, app_secret, appSecret, client_secret, clientSecret, scope, redirect_uri, return_url, connection_scene, onboarding_flow } = request;
    return this.invokeSupabaseFunction<FeishuConnectInitResponse>('feishu-oauth-init', {
      workspace_id,
      region,
      provider,
      app_id,
      appId,
      client_id,
      clientId,
      app_secret,
      appSecret,
      client_secret,
      clientSecret,
      scope,
      redirect_uri,
      return_url,
      connection_scene,
      onboarding_flow,
    });
  }

  /**
   * Feishu 完成连接：调用自建 OAuth callback 交换 token 并写入 workspace integration。
   */
  public async completeFeishuConnect(request: FeishuConnectCompleteRequest): Promise<FeishuConnectCompleteResponse> {
    const { code, state, redirect_uri } = request;
    return this.invokeSupabaseFunction<FeishuConnectCompleteResponse>('feishu-oauth-callback', {
      code,
      state,
      redirect_uri,
    });
  }

  /**
   * Feishu 断开连接。
   */
  public async disconnectFeishu(request: FeishuDisconnectRequest): Promise<FeishuDisconnectResponse> {
    const { workspace_id, tenant_key, connection_id, external_account_id } = request;
    return this.invokeSupabaseFunction<FeishuDisconnectResponse>('feishu-disconnect', {
      workspace_id,
      tenant_key,
      connection_id,
      external_account_id,
    });
  }

  /**
   * DingTalk 初始化连接：获取企业自建应用 OAuth 授权 URL。
   */
  public async initDingTalkConnect(request: DingTalkConnectInitRequest): Promise<DingTalkConnectInitResponse> {
    const { workspace_id, region, provider, app_id, appId, client_id, clientId, app_secret, appSecret, client_secret, clientSecret, scope, redirect_uri, return_url, connection_scene, onboarding_flow } = request;
    return this.invokeSupabaseFunction<DingTalkConnectInitResponse>('dingtalk-oauth-init', {
      workspace_id,
      region,
      provider,
      app_id,
      appId,
      client_id,
      clientId,
      app_secret,
      appSecret,
      client_secret,
      clientSecret,
      scope,
      redirect_uri,
      return_url,
      connection_scene,
      onboarding_flow,
    });
  }

  /**
   * DingTalk 完成连接：交换授权码并写入 workspace integration。
   */
  public async completeDingTalkConnect(request: DingTalkConnectCompleteRequest): Promise<DingTalkConnectCompleteResponse> {
    const { code, state, redirect_uri } = request;
    return this.invokeSupabaseFunction<DingTalkConnectCompleteResponse>('dingtalk-oauth-callback', {
      code,
      state,
      redirect_uri,
    });
  }

  /** DingTalk 断开连接。 */
  public async disconnectDingTalk(request: DingTalkDisconnectRequest): Promise<DingTalkDisconnectResponse> {
    const { workspace_id, corp_id, connection_id, external_account_id } = request;
    return this.invokeSupabaseFunction<DingTalkDisconnectResponse>('dingtalk-disconnect', {
      workspace_id,
      corp_id,
      connection_id,
      external_account_id,
    });
  }

  /**
   * 获取工作区下的所有集成
   * @param axiosRequestConfig - Optional axios config (must include X-Workspace-Id header)
   * @returns Promise with workspace integrations data
   */
  public async getWorkspaceIntegrations(axiosRequestConfig?: AxiosRequestConfig): Promise<GetWorkspaceIntegrationsResponse> {
    try {
      const response = await this.api.get<GetWorkspaceIntegrationsResponse>('/api/workspaces/integrations', {
        ...axiosRequestConfig,
        timeout: axiosRequestConfig?.timeout ?? 90000,
      });
      return response;
    } catch (error) {
      console.error('Get workspace integrations failed:', error);
      throw error;
    }
  }

  public async createBustlyConnectSession(
    request: CreateBustlyConnectSessionRequest
  ): Promise<CreateBustlyConnectSessionResponse> {
    const { axiosRequestConfig, ...body } = request;
    return this.api.post<CreateBustlyConnectSessionResponse>(
      '/api/workspaces/integrations/connect-session',
      body,
      axiosRequestConfig
    );
  }

  public async registerBustlyIntegration(
    request: RegisterBustlyIntegrationRequest
  ): Promise<WorkspaceIntegrationMapping> {
    const { axiosRequestConfig, ...connection } = request;
    return this.api.post<WorkspaceIntegrationMapping>(
      '/api/workspaces/integrations',
      {
        connectionBackend: 'BUSTLY',
        ...connection,
      },
      axiosRequestConfig
    );
  }

  public async disconnectBustlyIntegration(
    integrationId: number | string,
    axiosRequestConfig?: AxiosRequestConfig
  ): Promise<void> {
    return this.api.delete<void>(
      `/api/workspaces/integrations/${encodeURIComponent(String(integrationId))}`,
      axiosRequestConfig
    );
  }

  public async getWorkspaceIntegrationAccessGrants(
    integrationId: number | string,
    axiosRequestConfig?: AxiosRequestConfig
  ): Promise<WorkspaceConnectionAccessGrantsResponse> {
    return this.api.get<WorkspaceConnectionAccessGrantsResponse>(
      `/api/workspaces/integrations/${integrationId}/access`,
      axiosRequestConfig
    );
  }

  public async updateWorkspaceIntegrationAccessGrants(
    integrationId: number | string,
    { grants, axiosRequestConfig }: UpdateWorkspaceConnectionAccessGrantsRequest
  ): Promise<WorkspaceConnectionAccessGrantsResponse> {
    return this.api.put<WorkspaceConnectionAccessGrantsResponse>(
      `/api/workspaces/integrations/${integrationId}/access`,
      { grants },
      axiosRequestConfig
    );
  }

  public async verifyManualApiKeyIntegration(request: ManualApiKeyConnectRequest, axiosRequestConfig?: AxiosRequestConfig): Promise<Record<string, any>> {
    return this.api.post<Record<string, any>>('/api/workspaces/integrations/manual-api-keys/verify', request, axiosRequestConfig);
  }

  public async connectManualApiKeyIntegration(request: ManualApiKeyConnectRequest, axiosRequestConfig?: AxiosRequestConfig): Promise<Record<string, any>> {
    return this.api.post<Record<string, any>>('/api/workspaces/integrations/manual-api-keys/connect', request, axiosRequestConfig);
  }

  public async disconnectManualApiKeyIntegration(provider: string, axiosRequestConfig?: AxiosRequestConfig, integrationId?: string | number): Promise<void> {
    const integrationIdQuery = integrationId == null ? '' : `?integrationId=${encodeURIComponent(String(integrationId))}`;
    return this.api.delete<void>(`/api/workspaces/integrations/manual-api-keys/${provider}${integrationIdQuery}`, axiosRequestConfig);
  }

  public async updateWorkspaceIntegrationLabel(integrationId: number | string, { label, axiosRequestConfig }: UpdateWorkspaceIntegrationLabelRequest): Promise<UpdateWorkspaceIntegrationLabelResponse> {
    return this.api.put<UpdateWorkspaceIntegrationLabelResponse>(`/api/workspaces/integrations/${integrationId}/label`, { label }, axiosRequestConfig);
  }

  public async updateGoogleAdsCustomerLabel(integrationId: number | string, customerId: number | string, { label, axiosRequestConfig }: UpdateGoogleAdsCustomerLabelRequest): Promise<UpdateGoogleAdsCustomerLabelResponse> {
    return this.api.put<UpdateGoogleAdsCustomerLabelResponse>(`/api/workspaces/integrations/${integrationId}/google-ads/customers/${encodeURIComponent(String(customerId))}/label`, { label }, axiosRequestConfig);
  }

  public async updateWorkspaceIntegrationResourceLabel(
    integrationId: number | string,
    { resourceType, resourceKey, label, axiosRequestConfig }: UpdateWorkspaceIntegrationResourceLabelRequest,
  ): Promise<UpdateWorkspaceIntegrationResourceLabelResponse> {
    return this.api.put<UpdateWorkspaceIntegrationResourceLabelResponse>(
      `/api/workspaces/integrations/${integrationId}/resources/label`,
      { resourceType, resourceKey, label },
      axiosRequestConfig,
    );
  }

  public async getOnboardingWorkspaceResume(): Promise<OnboardingWorkspaceResumeResponse> {
    try {
      return await this.api.get<OnboardingWorkspaceResumeResponse>('/api/workspaces/onboarding-resume', {
        headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
      });
    } catch (error) {
      console.error('Get onboarding workspace resume failed:', error);
      throw error;
    }
  }

  public async getPluginOnboardingWorkspaceResume(): Promise<OnboardingWorkspaceResumeResponse> {
    try {
      return await this.api.get<OnboardingWorkspaceResumeResponse>('/api/products/plugin/workspaces/onboarding-resume', {
        headers: {
          [SKIP_WORKSPACE_ID_HEADER]: 'true',
          [PRODUCT_CONTEXT_HEADER]: PLUGIN_PRODUCT_CODE,
        },
      });
    } catch (error) {
      console.error('Get plugin onboarding workspace resume failed:', error);
      throw error;
    }
  }

  public async getPluginWorkspaces(): Promise<ProductWorkspace[]> {
    try {
      return await this.api.get<ProductWorkspace[]>('/api/products/plugin/workspaces', withPluginProductHeaders({
        headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
      }));
    } catch (error) {
      console.error('Get plugin workspaces failed:', error);
      throw error;
    }
  }

  public async getCoreWorkspaces(): Promise<ProductWorkspace[]> {
    try {
      return await this.api.get<ProductWorkspace[]>('/api/workspaces', withProductHeaders(BUSTLY_CORE_PRODUCT_CODE, {
        headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
      }));
    } catch (error) {
      console.error('Get Core workspaces failed:', error);
      throw error;
    }
  }

  public async getDefaultPluginWorkspace(): Promise<ProductWorkspace | null> {
    try {
      return await this.api.get<ProductWorkspace | null>('/api/products/plugin/workspaces/default', withPluginProductHeaders({
        headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
      }));
    } catch (error) {
      console.error('Get default plugin workspace failed:', error);
      throw error;
    }
  }

  public async setDefaultPluginWorkspace(workspaceId: string): Promise<ProductWorkspace | null> {
    try {
      return await this.api.post<ProductWorkspace | null>(
        '/api/products/plugin/workspaces/default',
        { workspace_id: workspaceId },
        withPluginProductHeaders({
          headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
        }),
      );
    } catch (error) {
      console.error('Set default plugin workspace failed:', error);
      throw error;
    }
  }

  public async issueCloudAgentExchangeToken({ workspaceId, axiosRequestConfig }: CloudAgentExchangeTokenRequest): Promise<CloudAgentExchangeTokenResponse> {
    return this.api.post<CloudAgentExchangeTokenResponse>(
      '/api/v1/cloud-agent/exchange-token',
      {
        workspaceId,
      },
      axiosRequestConfig,
    );
  }

  public async getCloudAgentConnectBundle({ workspaceId, axiosRequestConfig }: CloudAgentConnectBundleRequest): Promise<CloudAgentConnectBundleResponse> {
    const requestPath = '/api/v1/cloud-agent/connect-bundle';
    const mergedAxiosRequestConfig: AxiosRequestConfig = {
      ...axiosRequestConfig,
      headers: {
        ...(axiosRequestConfig?.headers || {}),
        'X-Workspace-Id': workspaceId,
      },
    };
    console.info('[cloud-agent] Requesting connect bundle.', {
      requestPath,
      workspaceId,
    });
    try {
      const response = await this.api.post<CloudAgentConnectBundleResponse>(
        requestPath,
        {
          workspaceId,
        },
        mergedAxiosRequestConfig,
      );
      console.info('[cloud-agent] Connect bundle resolved.', {
        workspaceId: response.workspaceId,
        routingMode: response.routingMode,
        wsUrl: response.wsUrl,
        hasGatewayToken: Boolean(response.gatewayToken),
        expiresAt: response.expiresAt,
      });
      return response;
    } catch (error) {
      console.error('[cloud-agent] Connect bundle request failed.', {
        workspaceId,
        error,
      });
      throw error;
    }
  }

  /**
   * 获取 OAuth 最终回传地址，但不立即导航。
   * v2 接口会返回 code/state，这里负责把它们拼成客户端 callback URL。
   */
  public async getOAuthAuthorizeRedirect(
    workspaceId?: string | null,
    preferredTarget: OAuthAuthorizeCallbackTarget = 'auto',
    oauthContextOverride?: OAuthContext | null,
  ): Promise<string | null> {
    console.log('[getOAuthAuthorizeRedirect] 开始执行，检查 localStorage 中的 oauth_context');

    const oauthContextStr = oauthContextOverride
      ? null
      : getLocalStorageItem('bustly_oauth_context');
    console.log('[getOAuthAuthorizeRedirect] oauth_context 来源:', oauthContextOverride ? 'override' : 'localStorage');

    if (!oauthContextOverride && !oauthContextStr) {
      console.warn('[getOAuthAuthorizeRedirect] ⚠️ localStorage 中不存在 oauth_context，跳过 OAuth 授权请求');
      return null;
    }

    const oauthContext: OAuthContext = oauthContextOverride ?? JSON.parse(oauthContextStr as string);
    if (!oauthContext.client_id || !oauthContext.login_trace_id) {
      console.warn('[getOAuthAuthorizeRedirect] OAuth context 缺少 client_id 或 login_trace_id');
      return null;
    }

    // 构建 URL 参数
    const params = new URLSearchParams();
    if (oauthContext.client_id) {
      params.append('client_id', oauthContext.client_id);
    }
    if (oauthContext.redirect_uri) {
      params.append('redirect_uri', oauthContext.redirect_uri);
    }
    if (oauthContext.fallback_redirect_uri) {
      params.append('fallback_redirect_uri', oauthContext.fallback_redirect_uri);
    }
    if (oauthContext.device_id) {
      params.append('device_id', oauthContext.device_id);
    }
    if (oauthContext.login_trace_id) {
      params.append('login_trace_id', oauthContext.login_trace_id);
    }

    const url = `/api/oauth/authorize/v2?${params.toString()}`;
    console.log('[getOAuthAuthorizeRedirect] 🚀 准备发送 OAuth 授权请求:', url);

    // 获取 access token 和 workspace_id
    const sessionData = await supabase.auth.getSession();
    const accessToken = sessionData.data.session?.access_token;
    console.log('[getOAuthAuthorizeRedirect] access_token:', accessToken ? '存在' : '不存在');
    console.log('[getOAuthAuthorizeRedirect] workspace_id:', workspaceId);
    console.log('[getOAuthAuthorizeRedirect] login_trace_id:', oauthContext.login_trace_id);

    // 使用 axios 直接请求，设置 maxRedirects: 0 阻止自动跟随重定向
    const response = await axios.get(`${config.services.auth.apiHost}${url}`, {
      maxRedirects: 0, // 不自动跟随重定向
      validateStatus: (status) => status >= 200 && status < 400, // 接受 2xx 和 3xx 状态码
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...(workspaceId && { 'X-Workspace-Id': workspaceId }),
        ...(oauthContext.login_trace_id && {
          'X-Login-Trace-Id': oauthContext.login_trace_id,
        }),
      },
    });

    const responseData = response.data as OAuthAuthorizeResponse | null | undefined;
    const redirectUrl = resolveOAuthAuthorizeCallbackUrl(oauthContext, responseData, response.headers['location'] || response.headers['Location'] || null, preferredTarget);
    console.log('[getOAuthAuthorizeRedirect] 响应状态:', response.status);
    console.log('[getOAuthAuthorizeRedirect] 响应 body:', responseData);
    console.log('[getOAuthAuthorizeRedirect] 响应 redirect URL:', redirectUrl);
    return redirectUrl;
  }

  /**
   * 获取 OAuth 授权码
   * 从 localStorage 中读取 oauth_context 参数并请求授权
   * 使用 AJAX 请求但不跟随重定向，手动提取 location 头并跳转
   */
  public async getOAuthAuthorize(workspaceId?: string | null): Promise<void> {
    console.log('[getOAuthAuthorize] 开始执行，准备跳转到 OAuth 授权地址');

    try {
      const redirectUrl = await this.getOAuthAuthorizeRedirect(workspaceId);

      // 删除 bustly_oauth_context，避免页面后续重复触发授权
      removeLocalStorageItem('bustly_oauth_context');
      console.log('[getOAuthAuthorize] 🗑️ 已删除 localStorage 中的 bustly_oauth_context');

      if (redirectUrl) {
        console.log('[getOAuthAuthorize] ✅ 跳转到授权地址:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.warn('[getOAuthAuthorize] ⚠️ 未找到重定向地址');
      }
    } catch (error: any) {
      console.error('[getOAuthAuthorize] ❌ 获取 OAuth 授权码失败:', error);
      // 即使失败也删除 bustly_oauth_context
      removeLocalStorageItem('bustly_oauth_context');
      throw error;
    }
  }
}

// 创建 AuthService 实例
export const authService = new AuthService({
  baseURL: config.services.auth.apiHost,
  headers: { 'Content-Type': 'application/json' },
});
